import { useTranslation } from "react-i18next";
// src/pages/BankAccountList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiGet } from "../api/client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import { A } from "../styles/appleClasses";

interface Account {
  id: number;
  name: string;
  type: string;
  is_system?: boolean;
  kind?: string;
}

// Root E: one row of GET /reports/trial-balance. `balance` is the account's
// computed ledger balance (Dr - Cr) summed across EVERY journal source type,
// which is why it covers both ordinary activity and opening entries posted as
// `opening_cash_bank_migration` by the existing-business onboarding wizard.
type TrialBalanceRow = {
  account_id: number;
  balance: number;
};

type TrialBalancePayload = { rows?: TrialBalanceRow[] } | TrialBalanceRow[];

// Root E: the balance is fetched independently of the account list, so it has
// its own settle state. "error" covers every non-2xx -- including the 403 a
// platform super-admin who is not a company admin receives from the
// company-admin-gated report endpoint. A balance is NEVER inferred: an absent
// row and a failed request both render the unavailable placeholder, never 0.
type BalanceState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; byAccountId: Map<number, number> };

const UNAVAILABLE = "—";
const PENDING = "…";

// Correct sign placement for money: -$120.00, never $-120.00. Defined locally
// (and identically in EditBankAccount.tsx) because Root E's authorized scope is
// these two components; the shared formatNumber/formatMoney helpers place the
// currency symbol before the minus sign.
function formatSignedMoney(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function BankAccountList() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [balances, setBalances] = useState<BalanceState>({ status: "loading" });
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  useEffect(() => {
    if (!company_id) return;
    apiGet<Account[]>(`/accounts?company_id=${company_id}`).then((data) =>
      setAccounts(Array.isArray(data) ? data : []),
    );
  }, [company_id]);

  // Root E: a SEPARATE effect, deliberately not combined with the account fetch
  // and never gated behind a shared Promise.all -- a slow, failing or forbidden
  // balance request must not delay or block rendering the account names.
  //
  // No as-of date parameter is sent. The server defaults its cutoff to
  // date.today() (trial_balance_router.py), the same naive server-local
  // business date every other accounting surface uses. Sending a
  // browser-computed date would make this balance disagree with the Trial
  // Balance report for any user outside the server's timezone.
  useEffect(() => {
    if (!company_id) return;
    let alive = true;
    setBalances({ status: "loading" });

    apiGet<TrialBalancePayload>("/reports/trial-balance")
      .then((payload) => {
        if (!alive) return;
        const rows = Array.isArray(payload) ? payload : payload?.rows ?? [];
        const byAccountId = new Map<number, number>();
        for (const row of rows || []) {
          if (
            typeof row?.account_id === "number" &&
            typeof row?.balance === "number" &&
            !Number.isNaN(row.balance)
          ) {
            byAccountId.set(row.account_id, row.balance);
          }
        }
        setBalances({ status: "ready", byAccountId });
      })
      .catch(() => {
        if (alive) setBalances({ status: "error" });
      });

    return () => {
      alive = false;
    };
  }, [company_id]);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      const k = (acc.kind || "").toLowerCase();
      return (k === "cash" || k === "bank") && acc.is_system !== true;
    });
  }, [accounts]);

  // A returned 0 renders 0.00. A missing row or a failed/forbidden request
  // renders the unavailable placeholder -- never 0, and never a stored scalar.
  const balanceText = (accountId: number): string => {
    if (balances.status === "loading") return PENDING;
    if (balances.status === "error") return UNAVAILABLE;
    const value = balances.byAccountId.get(accountId);
    return value === undefined ? UNAVAILABLE : formatSignedMoney(value);
  };

  // AppleClasses
  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  // ✅ Match SalesList header behavior: border on the sticky bar + mb-2 OUTSIDE the border
  const headerBar =
    "sticky top-0 z-30 " +
    "bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 " +
    "border-b border-gray-100 " +
    "px-2 sm:px-6 pt-4 pb-2 mb-2";

  const cardBtn =
    `${A.card} w-full text-left ` +
    "px-5 py-4 flex items-center justify-between gap-3 " +
    "hover:shadow-md hover:border-gray-300 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const emptyCard = `${A.card} px-5 py-10 text-center ${A.label}`;

  // ✅ + Add (blue outline / blue border)
  const addBtn = `${A.btnBase} ${A.btnOutline}`;

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header */}
      <div className={headerBar}>
        <div className="w-full max-w-md mx-auto flex items-center">
          <button
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
            type="button"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h2 className={`flex-1 text-center ${A.title}`}>
            {t("bankAccounts.title")}
          </h2>

          <button
            onClick={() => navigate(companyRoute(company_id, "/settings"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
            type="button"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-md mx-auto px-2 sm:px-6">
        {/* Add */}
        <div className="flex justify-end mb-3">
          <Link
            to={companyRoute(company_id, "/settings/accounts/new")}
            className={addBtn}
          >
            {t("expenses.categories.add")}
          </Link>
        </div>

        {/* List */}
        <div className="flex flex-col gap-3">
          {filteredAccounts.length === 0 ? (
            <div className={emptyCard}>{t("bankAccounts.empty")}</div>
          ) : (
            <>
              {/* Root E: the amount column used to be unlabelled. Label it, so
                  the number is identified as the account's ledger balance. */}
              <div className="flex items-center justify-between px-5">
                <span className={A.label}>{t("bankAccounts.account")}</span>
                <span className={A.label}>{t("generalLedger.balance")}</span>
              </div>

              {filteredAccounts.map((acc) => (
                <button
                  key={acc.id}
                  onClick={() =>
                    navigate(
                      companyRoute(
                        company_id,
                        `/settings/accounts/${acc.id}/edit`,
                      ),
                    )
                  }
                  className={cardBtn}
                  type="button"
                >
                  <div className="min-w-0">
                    <div className={`${A.title} truncate`}>{acc.name}</div>
                  </div>

                  <div className={`${A.body} ${A.num} font-medium shrink-0`}>
                    <span className="sr-only">{t("generalLedger.balance")}</span>
                    {balanceText(acc.id)}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
